import { useTranslation } from 'react-i18next';

import type { Grade } from './site.js';

/**
 * A score over the days as a thin line (site.css .line): the side panel's Health Score with the
 * floor of the next grade dashed, an area's score on its page (site-area.html).
 */
export function ScoreLine({
  points,
  label,
  floor = null,
  width = 260,
}: {
  points: readonly number[];
  /** What the line shows, read out instead of the drawing. */
  label: string;
  /** The lowest score of the next grade, dashed with its name. */
  floor?: { score: number; grade: Grade } | null;
  width?: number;
}) {
  const { t } = useTranslation();
  const w = width;
  const h = 48;
  const values = floor === null ? points : [...points, floor.score];
  const lo = Math.max(0, Math.min(...values) - 5);
  const hi = Math.min(100, Math.max(...values) + 5);
  const y = (v: number) => h - 4 - ((v - lo) / Math.max(1, hi - lo)) * (h - 8);
  const x = (i: number) => (i * w) / Math.max(1, points.length - 1);
  // The name of the floor is text over the drawing: the drawing stretches with its card, text in
  // it would stretch along (SP-UX13).
  return (
    <div className="line" role="img" aria-label={label}>
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
        {floor ? (
          <line
            x1="0"
            x2={w}
            y1={y(floor.score)}
            y2={y(floor.score)}
            stroke={`var(--g-${floor.grade.toLowerCase()})`}
            strokeDasharray="4 4"
          />
        ) : null}
        <path
          d={points.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i)} ${y(v)}`).join(' ')}
          fill="none"
          stroke="var(--acc)"
          strokeWidth="1.5"
        />
      </svg>
      {floor ? (
        <span className="floor-label" style={{ top: `${(y(floor.score) / h) * 100}%` }}>
          {t('rail.floor', { grade: floor.grade, score: floor.score })}
        </span>
      ) : null}
    </div>
  );
}
